// src/pages/SR_AdminTasks.jsx
import React, { useEffect, useMemo, useState } from "react";
import { sr } from "../api/serviceReports";

export default function SR_AdminTasks() {
  const [rows, setRows] = useState([]);

  // create fields
  const [title, setTitle] = useState("");
  const [hasText, setHasText] = useState(false);
  const [sort, setSort] = useState(0);

  // edit state (per row)
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editHasText, setEditHasText] = useState(false);
  const [editSort, setEditSort] = useState(0);

  // templates usage: { taskId: [templateName, ...] }
  const [usedBy, setUsedBy] = useState({});

  const [msg, setMsg] = useState(null); // {text, error?}
  const labelS = { fontSize: 12, fontWeight: 600, marginBottom: 4 };

  // ---------- load tasks + usage ----------
  const load = async () => {
    try {
      const list = await sr.tasks.list();
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setMsg({ text: `Load failed: ${e.message || e}`, error: true });
    }
  };

  // Build a map of which tasks are used by which templates
  const loadUsage = async () => {
    try {
      // get all templates (active + inactive if your backend supports ?all=1)
      const tpls = await sr.templates.list(true).catch(() => sr.templates.list());
      const detailed = await Promise.all(
        (Array.isArray(tpls) ? tpls : []).map(async (t) => {
          try {
            const full = await sr.templates.get(t.id);
            return { name: t.name, tasks: full?.tasks || [] , task_ids: full?.task_ids || [] };
          } catch {
            return { name: t.name, tasks: [], task_ids: [] };
          }
        })
      );

      const map = {};
      for (const tpl of detailed) {
        if (Array.isArray(tpl.tasks) && tpl.tasks.length) {
          for (const x of tpl.tasks) {
            const id = Number(x.task_id);
            if (!map[id]) map[id] = [];
            if (!map[id].includes(tpl.name)) map[id].push(tpl.name);
          }
        } else if (Array.isArray(tpl.task_ids) && tpl.task_ids.length) {
          for (const id of tpl.task_ids) {
            const k = Number(id);
            if (!map[k]) map[k] = [];
            if (!map[k].includes(tpl.name)) map[k].push(tpl.name);
          }
        }
      }
      setUsedBy(map);
    } catch (e) {
      // Non-fatal; deletion protection will be skipped if we cannot resolve usage
      console.warn("Could not load template usage:", e);
      setUsedBy({});
    }
  };

  useEffect(() => {
    load();
    loadUsage();
  }, []);

  // ---------- helpers ----------
  const existingNames = useMemo(
    () => new Map(rows.map((r) => [String(r.title || "").trim().toLowerCase(), r.id])),
    [rows]
  );

  function nameExists(name, excludeId = null) {
    const key = String(name || "").trim().toLowerCase();
    if (!key) return false;
    const foundId = existingNames.get(key);
    return foundId !== undefined && foundId !== excludeId;
  }

  // ---------- add ----------
  async function onAdd() {
    const trimmed = title.trim();
    if (!trimmed) {
      setMsg({ text: "Title is required.", error: true });
      return;
    }
    if (nameExists(trimmed)) {
      setMsg({ text: `Duplicate task name: “${trimmed}” already exists.`, error: true });
      return;
    }
    try {
      await sr.tasks.add({
        title: trimmed,
        has_text: hasText ? 1 : 0,
        sort_order: Number(sort) || 0,
        active: 1,
      });
      setTitle(""); setHasText(false); setSort(0);
      setMsg({ text: "Task added." });
      await load();
    } catch (e) {
      setMsg({ text: `Add failed: ${e.message || e}`, error: true });
    }
  }

  // ---------- enable/disable ----------
  async function toggleActive(t) {
    try {
      await sr.tasks.update(t.id, { ...t, active: t.active ? 0 : 1 });
      await load();
    } catch (e) {
      setMsg({ text: `Update failed: ${e.message || e}`, error: true });
    }
  }

  // ---------- edit ----------
  function startEdit(t) {
    setEditingId(t.id);
    setEditTitle(t.title || "");
    setEditHasText(!!t.has_text);
    setEditSort(Number(t.sort_order) || 0);
  }
  function cancelEdit() {
    setEditingId(null);
    setEditTitle("");
    setEditHasText(false);
    setEditSort(0);
  }
  async function saveEdit(id) {
    const trimmed = editTitle.trim();
    if (!trimmed) { setMsg({ text: "Title is required.", error: true }); return; }
    if (nameExists(trimmed, id)) {
      setMsg({ text: `Duplicate task name: “${trimmed}” already exists.`, error: true });
      return;
    }
    try {
      await sr.tasks.update(id, {
        title: trimmed,
        has_text: editHasText ? 1 : 0,
        sort_order: Number(editSort) || 0,
      });
      setMsg({ text: "Task updated." });
      cancelEdit();
      await load();
      await loadUsage(); // usage may change if templates display titles anywhere later
    } catch (e) {
      setMsg({ text: `Save failed: ${e.message || e}`, error: true });
    }
  }

  // ---------- delete with protection ----------
  async function onDelete(t) {
    const refs = usedBy[t.id] || [];
    if (refs.length) {
      const list = refs.join(", ");
      setMsg({
        text: `Cannot delete “${t.title}”: it’s used in template${refs.length > 1 ? "s" : ""} ${list}. Remove it from those template(s) first.`,
        error: true,
      });
      return;
    }
    if (!window.confirm(`Delete task "${t.title}"? This cannot be undone.`)) return;
    try {
      if (!sr.tasks.remove) throw new Error('Missing sr.tasks.remove(id). Add DELETE /tasks/:id in serviceReports.js');
      await sr.tasks.remove(t.id);
      setMsg({ text: "Task deleted." });
      await load();
    } catch (e) {
      setMsg({ text: `Delete failed: ${e.message || e}`, error: true });
    }
  }

  return (
    <div className="card">
      <h2>Checklist Tasks</h2>

      {msg && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            borderRadius: 8,
            border: `1px solid ${msg.error ? "#fecaca" : "#cce3d1"}`,
            background: msg.error ? "#fee2e2" : "#e7f6ec",
            color: msg.error ? "#991b1b" : "#135f2b",
          }}
        >
          {msg.text}
        </div>
      )}

      {/* Add new task */}
      <div className="row">
        <div style={{ flex: 1 }}>
          <div style={labelS}>Title</div>
          <input
            className="input"
            placeholder="Task title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <label style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 22 }}>
          <input
            type="checkbox"
            checked={hasText}
            onChange={(e) => setHasText(e.target.checked)}
          />
          Has text box
        </label>
        <div>
          <div style={labelS}>Sort</div>
          <input
            className="input"
            type="number"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            placeholder="Sort"
            style={{ width: 120 }}
          />
        </div>
        <div style={{ alignSelf: "end" }}>
          <button
            className="btn"
            onClick={onAdd}
            disabled={!title.trim() || nameExists(title)}
            title={nameExists(title) ? "Duplicate name" : ""}
          >
            Add
          </button>
        </div>
      </div>

      {/* List */}
      <table className="table" style={{ marginTop: 16 }}>
        <thead>
          <tr>
            <th style={{ minWidth: 240 }}>Title</th>
            <th>Has text</th>
            <th>Sort</th>
            <th>Status</th>
            <th style={{ width: 360, textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => {
            const editing = editingId === t.id;
            const inUseBy = usedBy[t.id] || [];
            const deleteDisabled = inUseBy.length > 0;

            const editDup = editing && nameExists(editTitle, t.id);

            return (
              <tr key={t.id}>
                <td>
                  {editing ? (
                    <input
                      className="input"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                    />
                  ) : (
                    t.title
                  )}
                </td>
                <td>
                  {editing ? (
                    <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={editHasText}
                        onChange={(e) => setEditHasText(e.target.checked)}
                      />
                      Yes
                    </label>
                  ) : t.has_text ? "Yes" : "No"}
                </td>
                <td style={{ width: 110 }}>
                  {editing ? (
                    <input
                      className="input"
                      type="number"
                      value={editSort}
                      onChange={(e) => setEditSort(e.target.value)}
                      style={{ width: 100 }}
                    />
                  ) : (
                    t.sort_order
                  )}
                </td>
                <td>
                  <span className="badge">{t.active ? "Active" : "Inactive"}</span>
                </td>
                <td style={{ textAlign: "right" }}>
                  {editing ? (
                    <>
                      <button
                        className="btn"
                        onClick={() => saveEdit(t.id)}
                        disabled={!editTitle.trim() || editDup}
                        title={editDup ? "Duplicate name" : ""}
                      >
                        Save
                      </button>{" "}
                      <button className="btn secondary" onClick={cancelEdit}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="btn" onClick={() => startEdit(t)}>Edit</button>{" "}
                      <button className="btn" onClick={() => toggleActive(t)}>
                        {t.active ? "Disable" : "Enable"}
                      </button>{" "}
                      <button
                        className="btn cancel"
                        onClick={() => onDelete(t)}
                        disabled={deleteDisabled}
                        title={
                          deleteDisabled
                            ? `Used by template${inUseBy.length > 1 ? "s" : ""}: ${inUseBy.join(", ")}`
                            : ""
                        }
                      >
                        Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={5} style={{ padding: 12, color: "#666" }}>
                No tasks yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
